import { FC, useMemo, useState } from 'react'
import {
  Box,
  ButtonBase,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

import {
  NotificationsListResponse,
  useNotificationsList
} from 'services/notifications/notifications.hook'
import { NOTIFICATION_STATUS_TYPE } from 'services/notifications/notifications.consts'

const FILTERS = [
  { value: false, i18n: { id: 'notificationsList.all', defaultMessage: 'Wszystkie' } },
  { value: true, i18n: { id: 'notificationsList.unread', defaultMessage: 'Nieprzeczytane' } }
] as const

const getNotificationDate = (notification: NotificationsListResponse) => {
  const raw = notification.createDate || notification.createdAt || notification.created
  if (!raw) {
    return undefined
  }
  const parsed = dayjs(raw)
  return parsed.isValid() ? parsed.format('DD.MM') : undefined
}

// Notifications master list ("Panel Klienta 3.0" Powiadomienia).
const NotificationsList: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [hasUnread, setUnread] = useState(false)
  const { formatMessage } = useIntl()
  const { notificationsList, isLoading } = useNotificationsList()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const selectedId = matchPath('/notifications/:id', pathname)?.params.id

  const notifications = useMemo(() => {
    if (hasUnread) {
      return notificationsList.filter(({ status }) => status === NOTIFICATION_STATUS_TYPE.unread)
    }
    return notificationsList
  }, [hasUnread, notificationsList])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isDesktop ? '100%' : 'auto',
        backgroundColor: '#fff'
      }}>
      {isDesktop && (
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '24px',
            color: theme.r4r.ink,
            px: '28px',
            pt: '24px',
            pb: '20px'
          }}>
          {formatMessage({
            id: 'notificationsPage.notifications',
            defaultMessage: 'Powiadomienia'
          })}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          px: isDesktop ? '28px' : '16px',
          pb: '17px',
          borderBottom: `1px solid ${theme.r4r.greyLight}`
        }}>
        {FILTERS.map(({ value, i18n }) => {
          const isActive = hasUnread === value
          return (
            <ButtonBase
              key={String(value)}
              onClick={() => setUnread(value)}
              sx={{
                px: '19px',
                py: '9px',
                fontFamily: theme.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '13px',
                whiteSpace: 'nowrap',
                border: `1px solid ${isActive ? theme.r4r.navy : theme.r4r.greyLight}`,
                backgroundColor: isActive ? theme.r4r.navy : '#fff',
                color: isActive ? '#fff' : theme.r4r.grey,
                ml: value ? '-1px' : 0
              }}>
              {formatMessage(i18n)}
            </ButtonBase>
          )
        })}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {!notifications.length ? (
          <Typography
            sx={{
              fontSize: '14px',
              color: theme.r4r.grey,
              textAlign: 'center',
              px: '28px',
              py: '40px'
            }}>
            {hasUnread
              ? formatMessage({
                  id: 'notifications.allRead',
                  defaultMessage: 'Wszystkie powiadomienia zostały przeczytane.'
                })
              : formatMessage({
                  id: 'notificationsList.noNotifications',
                  defaultMessage: 'Brak powiadomień.'
                })}
          </Typography>
        ) : (
          notifications.map((notification) => {
            const { id, title, status } = notification
            const isUnread = status === NOTIFICATION_STATUS_TYPE.unread
            const isSelected = isDesktop && String(id) === selectedId
            const dateLabel = getNotificationDate(notification)

            return (
              <ButtonBase
                key={id}
                onClick={() => navigate(`/notifications/${id}`)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'left',
                  gap: '14px',
                  px: isDesktop ? '28px' : '16px',
                  pt: '18px',
                  pb: '19px',
                  borderBottom: `1px solid ${theme.r4r.greyLight}`,
                  backgroundColor: isSelected ? 'rgba(31, 31, 115, 0.04)' : 'transparent',
                  ':hover': { backgroundColor: 'rgba(31, 31, 115, 0.04)' }
                }}>
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    flexShrink: 0,
                    borderRadius: '4px',
                    backgroundColor: isUnread ? theme.r4r.azure : 'transparent'
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.r4r.ink,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {title}
                </Typography>
                {!!dateLabel && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 400,
                      color: theme.r4r.grey,
                      flexShrink: 0,
                      whiteSpace: 'nowrap'
                    }}>
                    {dateLabel}
                  </Typography>
                )}
              </ButtonBase>
            )
          })
        )}
      </Box>
    </Box>
  )
}

export default NotificationsList
